import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../api/client";
import AddToTradeQuantityControl from "./AddToTradeQuantityControl";
import CardAttributePill, { ConditionPill, LanguagePill } from "./CardAttributePill";
import BrowseUserIdentityLink from "./BrowseUserIdentityLink";
import normalizeCondition from "../utils/normalizeCondition";
import { buildBrowseUserPath } from "../utils/browseUserPath";

const DEFAULT_SORT = {
  key: "price",
  direction: "asc"
};

const SORTABLE_COLUMN_DEFAULT_DIRECTIONS = {
  trader: "asc",
  name: "asc",
  edition: "asc",
  language: "asc",
  condition: "asc",
  foil: "asc",
  altered: "asc",
  signed: "asc",
  price: "asc",
  trend: "desc",
  foilTrend: "desc"
};

const COMPACT_LAYOUT_BREAKPOINT = 920;

const sortButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  width: "100%",
  border: "none",
  background: "transparent",
  padding: 0,
  color: "#0f172a",
  fontSize: "12px",
  fontWeight: 700,
  cursor: "pointer",
  textAlign: "left"
};

const sortIndicatorStyle = {
  minWidth: "12px",
  color: "#64748b",
  fontWeight: 800
};

function formatCurrency(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  return `${numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} €`;
}

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function getDisplayBoolean(value, type) {
  if (!value) {
    return "-";
  }

  return <CardAttributePill type={type} title={type === "foil" ? "Foil" : type === "altered" ? "Altered" : "Signed"} />;
}

function getDisplayLanguage(card) {
  if (!card.language) {
    return "-";
  }

  return <LanguagePill language={card.language} />;
}

function getDisplayCondition(card) {
  const normalizedCondition = normalizeCondition(card.condition);

  if (!normalizedCondition) {
    return "-";
  }

  return <ConditionPill condition={normalizedCondition} />;
}

function getRarityColor(rarity) {
  const normalizedRarity = String(rarity || "").toLowerCase();

  if (normalizedRarity.includes("mythic")) return "#d97706";
  if (normalizedRarity.includes("rare")) return "#d4af37";
  if (normalizedRarity.includes("uncommon")) return "#9ca3af";
  if (normalizedRarity.includes("special") || normalizedRarity.includes("bonus")) return "#3b82f6";

  return "#000000";
}

function getSortableCardValue(card, key) {
  switch (key) {
    case "trader":
      return String(card?.trader_username || "").toLowerCase();
    case "name":
      return String(card?.card || card?.name || "").toLowerCase();
    case "edition":
      return String(card?.edition || "").toLowerCase();
    case "language":
      return String(card?.language || "").toLowerCase();
    case "condition":
      return String(normalizeCondition(card?.condition) || "").toLowerCase();
    case "foil":
      return card?.foil ? 1 : 0;
    case "altered":
      return card?.altered ? 1 : 0;
    case "signed":
      return card?.signed ? 1 : 0;
    case "price":
      return Number(card?.sale_price ?? card?.user_price) || 0;
    case "trend":
      return Number(card?.price_trend) || 0;
    case "foilTrend":
      return Number(card?.price_foil) || 0;
    default:
      return "";
  }
}

function compareSortableValues(left, right) {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base"
  });
}

export default function BrowseCardsResultsTable({
  cards,
  userId,
  searchApiBasePath = "/api/traders",
  getTradeActionState,
  onAddToTrade,
  addingTradeCardKey = ""
}) {
  const layoutRef = useRef(null);
  const [setMetadata, setSetMetadata] = useState({});
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const [isMobileLayout, setIsMobileLayout] = useState(() => (typeof window !== "undefined"
    ? window.innerWidth <= COMPACT_LAYOUT_BREAKPOINT
    : false));

  const handleSortChange = useCallback((key) => {
    setSortConfig((currentSort) => {
      if (currentSort.key === key) {
        return {
          key,
          direction: currentSort.direction === "asc" ? "desc" : "asc"
        };
      }

      return {
        key,
        direction: SORTABLE_COLUMN_DEFAULT_DIRECTIONS[key] || "asc"
      };
    });
  }, []);

  const getSortIndicator = useCallback((key) => {
    if (sortConfig.key !== key) {
      return "";
    }

    return sortConfig.direction === "asc" ? "↑" : "↓";
  }, [sortConfig.direction, sortConfig.key]);

  const getAriaSort = useCallback((key) => {
    if (sortConfig.key !== key) {
      return "none";
    }

    return sortConfig.direction === "asc" ? "ascending" : "descending";
  }, [sortConfig.direction, sortConfig.key]);

  const sortedCards = useMemo(() => {
    const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1;

    return [...cards].sort((leftCard, rightCard) => {
      const primaryComparison = compareSortableValues(
        getSortableCardValue(leftCard, sortConfig.key),
        getSortableCardValue(rightCard, sortConfig.key)
      );

      if (primaryComparison !== 0) {
        return primaryComparison * directionMultiplier;
      }

      return compareSortableValues(
        getSortableCardValue(leftCard, "name"),
        getSortableCardValue(rightCard, "name")
      );
    });
  }, [cards, sortConfig.direction, sortConfig.key]);

  const renderSortableHeader = useCallback((label, key) => (
    <button type="button" style={sortButtonStyle} onClick={() => handleSortChange(key)}>
      <span>{label}</span>
      <span style={sortIndicatorStyle}>{getSortIndicator(key)}</span>
    </button>
  ), [getSortIndicator, handleSortChange]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const updateLayoutMode = (width) => {
      setIsMobileLayout(width <= COMPACT_LAYOUT_BREAKPOINT);
    };

    if (typeof ResizeObserver === "function" && layoutRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        const nextWidth = entries[0]?.contentRect?.width;

        if (Number.isFinite(nextWidth)) {
          updateLayoutMode(nextWidth);
        }
      });

      resizeObserver.observe(layoutRef.current);
      updateLayoutMode(layoutRef.current.getBoundingClientRect().width);

      return () => resizeObserver.disconnect();
    }

    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${COMPACT_LAYOUT_BREAKPOINT}px)`);

    const handleMediaQueryChange = (event) => {
      setIsMobileLayout(event.matches);
    };

    setIsMobileLayout(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaQueryChange);
      return () => mediaQuery.removeEventListener("change", handleMediaQueryChange);
    }

    mediaQuery.addListener(handleMediaQueryChange);
    return () => mediaQuery.removeListener(handleMediaQueryChange);
  }, []);

  useEffect(() => {
    const setCodes = [...new Set(cards.map((card) => normalizeLookupValue(card.edition)).filter(Boolean))];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    apiFetch(`${searchApiBasePath}/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not fetch set metadata");
        }

        if (!isCancelled) {
          setSetMetadata((current) => ({
            ...current,
            ...(data.sets || {})
          }));
        }
      })
      .catch((error) => {
        console.error("Browse cards set metadata error:", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [cards, searchApiBasePath, setMetadata, userId]);

  if (isMobileLayout) {
    return (
      <div ref={layoutRef} style={{ minWidth: 0 }}>
        <div
          style={{
            overflow: "hidden",
            border: "1px solid #d7dce5",
            borderRadius: "12px",
            background: "#ffffff",
            boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)"
          }}
        >
          {sortedCards.map((card, index) => {
          const cardSetCode = normalizeLookupValue(card.edition);
          const cardSetMetadata = setMetadata[cardSetCode];
          const setSymbolColor = getRarityColor(card.rarity);
          const tradeActionState = typeof getTradeActionState === "function"
            ? getTradeActionState(card)
            : { disabled: true, label: "Unavailable", title: "Trade actions are not available." };
          const displayedQty = tradeActionState.availableQty ?? Math.max(1, Number(card.qty) || 1);
          const tradeActionKey = `${Number(card.user_id) || 0}:${Number(card.source_listing_id || card.id) || 0}`;
          const isAddingToTrade = addingTradeCardKey === tradeActionKey;

          return (
            <div
              key={`${card.id}:${card.user_id}`}
              style={{
                padding: "12px",
                borderBottom: index === sortedCards.length - 1 ? "none" : "1px solid #eef2f7",
                background: "#ffffff"
              }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) max-content auto", gap: "10px", alignItems: "center", minWidth: 0 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "0",
                    height: "auto",
                    padding: 0,
                    borderRadius: 0,
                    background: "transparent",
                    color: "#0f172a",
                    fontSize: "12px",
                    fontWeight: 600
                  }}
                >
                  {displayedQty}
                </span>
                <div style={{ minWidth: 0 }}>
                  <BrowseUserIdentityLink
                    to={buildBrowseUserPath(card.user_id, card.trader_username)}
                    state={{ userId: Number(card.user_id) || null, username: card.trader_username || `User ${card.user_id}`, collectorTier: card.trader_collector_tier || null }}
                    username={card.trader_username || `User ${card.user_id}`}
                    tier={card.trader_collector_tier}
                    truncateAt={10}
                    className="user-identity-link--truncate"
                    style={{ width: "100%" }}
                  />
                </div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    flexShrink: 0,
                    gap: "4px",
                    padding: "3px 8px",
                    borderRadius: "999px",
                    background: "#4caf50",
                    color: "white",
                    fontSize: "11px",
                    fontWeight: 700,
                    lineHeight: 1.35,
                    whiteSpace: "nowrap"
                  }}
                >
                  {formatCurrency(card.sale_price ?? card.user_price)}
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", minWidth: "26px", flexShrink: 0 }}>
                  <AddToTradeQuantityControl
                    cardName={card.card || card.name}
                    maxQty={tradeActionState.maxQty || card.qty}
                    disabled={tradeActionState.disabled || isAddingToTrade || typeof onAddToTrade !== "function"}
                    disabledReason={tradeActionState.tooltip || ""}
                    busy={isAddingToTrade}
                    onAdd={(selectedQty) => onAddToTrade?.(card, selectedQty)}
                  />
                </div>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center", marginTop: "10px", minWidth: 0 }}>
                {cardSetMetadata?.iconSvgMarkup ? (
                  <span
                    aria-hidden="true"
                    title={cardSetMetadata?.name || card.edition || "Unknown edition"}
                    style={{
                      width: "16px",
                      height: "16px",
                      flex: "0 0 auto",
                      color: setSymbolColor,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                    dangerouslySetInnerHTML={{ __html: cardSetMetadata.iconSvgMarkup }}
                  />
                ) : null}
                {getDisplayLanguage(card)}
                {getDisplayCondition(card)}
                {getDisplayBoolean(card.foil, "foil")}
                {getDisplayBoolean(card.altered, "altered")}
                {getDisplayBoolean(card.signed, "signed")}
              </div>
            </div>
          );
          })}
        </div>
      </div>
    );
  }

  return (
    <div ref={layoutRef} style={{ minWidth: 0 }}>
      <div
        style={{
          overflowX: "auto",
          border: "1px solid #d7dce5",
          borderRadius: "12px",
          background: "#ffffff",
          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)"
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
            minWidth: "920px"
          }}
        >
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "11%" }} />
        </colgroup>
        <thead>
          <tr style={{ background: "#f8fafc", textAlign: "left" }}>
            <th aria-sort={getAriaSort("trader")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Trader", "trader")}</th>
            <th aria-sort={getAriaSort("edition")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Edition", "edition")}</th>
            <th aria-sort={getAriaSort("language")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Language", "language")}</th>
            <th aria-sort={getAriaSort("condition")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Condition", "condition")}</th>
            <th aria-sort={getAriaSort("foil")} style={{ padding: "10px 10px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Foil?", "foil")}</th>
            <th aria-sort={getAriaSort("altered")} style={{ padding: "10px 10px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Altered?", "altered")}</th>
            <th aria-sort={getAriaSort("signed")} style={{ padding: "10px 10px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Signed?", "signed")}</th>
            <th aria-sort={getAriaSort("price")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Price", "price")}</th>
            <th aria-sort={getAriaSort("trend")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Trend", "trend")}</th>
            <th aria-sort={getAriaSort("foilTrend")} style={{ padding: "10px 12px", borderBottom: "1px solid #d7dce5", fontSize: "12px" }}>{renderSortableHeader("Foil Trend", "foilTrend")}</th>
            <th style={{ padding: "10px 8px", borderBottom: "1px solid #d7dce5", fontSize: "12px", color: "#0f172a", fontWeight: 700 }} />
          </tr>
        </thead>
        <tbody>
          {sortedCards.map((card) => {
            const cardSetCode = normalizeLookupValue(card.edition);
            const cardSetMetadata = setMetadata[cardSetCode];
            const setSymbolColor = getRarityColor(card.rarity);
            const tradeActionState = typeof getTradeActionState === "function"
              ? getTradeActionState(card)
              : { disabled: true, label: "Unavailable", title: "Trade actions are not available." };
            const tradeActionKey = `${Number(card.user_id) || 0}:${Number(card.source_listing_id || card.id) || 0}`;
            const isAddingToTrade = addingTradeCardKey === tradeActionKey;

            return (
              <tr key={`${card.id}:${card.user_id}`} style={{ background: "#ffffff" }}>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                  <BrowseUserIdentityLink
                    to={buildBrowseUserPath(card.user_id, card.trader_username)}
                    state={{ userId: Number(card.user_id) || null, username: card.trader_username || `User ${card.user_id}`, collectorTier: card.trader_collector_tier || null }}
                    username={card.trader_username || `User ${card.user_id}`}
                    tier={card.trader_collector_tier}
                    truncateAt={10}
                    className="user-identity-link--truncate"
                    style={{ display: "block", width: "100%", maxWidth: "100%", minWidth: 0, color: "#0f766e" }}
                  />
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px", textAlign: "left" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-start", width: "100%" }}>
                    {cardSetMetadata?.iconSvgMarkup ? (
                      <span
                        className="browse-cards-results-set-symbol"
                        aria-hidden="true"
                        title={cardSetMetadata?.name || card.edition || "Unknown edition"}
                        style={{
                          width: "24px",
                          height: "24px",
                          flex: "0 0 auto",
                          color: setSymbolColor,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                        dangerouslySetInnerHTML={{ __html: cardSetMetadata.iconSvgMarkup }}
                      />
                    ) : <span>-</span>}
                  </div>
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{getDisplayLanguage(card)}</td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{getDisplayCondition(card)}</td>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{getDisplayBoolean(card.foil, "foil")}</td>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{getDisplayBoolean(card.altered, "altered")}</td>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{getDisplayBoolean(card.signed, "signed")}</td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "4px",
                      padding: "3px 8px",
                      borderRadius: "999px",
                      background: "#4caf50",
                      color: "white",
                      fontSize: "11px",
                      fontWeight: 700,
                      lineHeight: 1.35
                    }}
                  >
                    {formatCurrency(card.sale_price ?? card.user_price)}
                  </span>
                </td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{formatCurrency(card.price_trend)}</td>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef2f7", fontSize: "12px" }}>{formatCurrency(card.price_foil)}</td>
                <td style={{ padding: "10px 8px", borderBottom: "1px solid #eef2f7", fontSize: "12px", textAlign: "center" }}>
                  <AddToTradeQuantityControl
                    cardName={card.card || card.name}
                    maxQty={tradeActionState.maxQty || card.qty}
                    disabled={tradeActionState.disabled || isAddingToTrade || typeof onAddToTrade !== "function"}
                    disabledReason={tradeActionState.tooltip || ""}
                    busy={isAddingToTrade}
                    onAdd={(selectedQty) => onAddToTrade?.(card, selectedQty)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>
    </div>
  );
}